import { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { getAllTiers, formatPrice, formatYearlyPrice } from '../../constants/tiers';
import { useCurrentOrganization } from '../../hooks/useCurrentOrganization';
import { useTabNavigationStore } from '../../store/tabNavigationStore';

export function SubscriptionPlansScreen() {
  const { data: organization } = useCurrentOrganization();
  const { pushScreen } = useTabNavigationStore();

  const currentTier = organization?.subscription_tier || 'free';
  const allTiers = getAllTiers();

  // Auto-renewal state for each tier (by tier ID)
  const [autoRenewalState, setAutoRenewalState] = useState<Record<string, boolean>>({});

  const handleAutoRenewalToggle = (tierId: string) => {
    setAutoRenewalState((prev) => ({
      ...prev,
      [tierId]: !prev[tierId],
    }));
  };

  const handleSelectPlan = (tierId: string) => {
    try {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    } catch {}

    // Navigate to tier change confirmation screen with auto-renewal flag
    pushScreen('tier-change-confirm', {
      newTierId: tierId,
      autoRenewal: autoRenewalState[tierId] || false,
    });
  };

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title="Изменить тариф"
        subtitle="Выберите подходящий тариф для вашей организации"
      />

      <div className="space-y-3">
        {allTiers.map((tier) => {
          const isCurrent = tier.id === currentTier;
          const isHighlighted = tier.highlight;
          const autoRenewal = autoRenewalState[tier.id] || false;

          return (
            <Card
              key={tier.id}
              className={`${isHighlighted ? 'border-2' : ''} min-h-[180px]`}
            >
              {/* Header: name + badge */}
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {tier.name}
                </h3>
                <div className="flex gap-1">
                  {isCurrent && (
                    <span
                      className="text-xs px-2 py-1 rounded"
                      style={{ backgroundColor: '#22c55e', color: '#fff' }}
                    >
                      Текущий
                    </span>
                  )}
                  {isHighlighted && !isCurrent && (
                    <span
                      className="text-xs px-2 py-1 rounded"
                      style={{ backgroundColor: 'var(--accent)', color: '#fff' }}
                    >
                      Популярный
                    </span>
                  )}
                </div>
              </div>

              {/* Price: compact */}
              <div className="mb-2">
                <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {tier.price === 0 ? 'Бесплатно' : formatPrice(tier.price)}
                </div>
                {tier.priceYearly && tier.priceYearly > 0 && (
                  <div className="text-xs" style={{ color: 'var(--text-hint)' }}>
                    {formatYearlyPrice(tier.priceYearly)}{' '}
                    <span style={{ color: '#22c55e' }}>(экономия 17%)</span>
                  </div>
                )}
              </div>

              {/* Top features: 2-column grid */}
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs mb-3">
                {tier.topFeatures.map((feature, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-1"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <span style={{ color: '#22c55e' }}>✓</span>
                    <span className="truncate">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Auto-renewal toggle (only for non-current paid tiers) */}
              {!isCurrent && tier.price > 0 && (
                <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRenewal}
                    onChange={() => handleAutoRenewalToggle(tier.id)}
                    className="w-4 h-4 accent-[var(--accent)]"
                  />
                  <span style={{ color: 'var(--text-primary)' }}>Автопродление</span>
                </label>
              )}

              {/* CTA button */}
              <Button
                onClick={() => handleSelectPlan(tier.id)}
                disabled={isCurrent}
                fullWidth
                variant={isCurrent ? 'secondary' : 'primary'}
              >
                {isCurrent ? 'Активен' : tier.price === 0 ? 'Связаться' : 'Выбрать'}
              </Button>
            </Card>
          );
        })}
      </div>

      <Card>
        <p className="text-sm text-center" style={{ color: 'var(--text-hint)' }}>
          Оплата производится через ЮKassa (банковская карта)
        </p>
      </Card>
    </div>
  );
}
